import { useContext, useEffect, useRef, useState } from "react";

import { getUserAccounts } from "~/api";
import { useTranslation } from "~/hooks/useTranslation";
import { ENameSpace } from "~/i18n/@types/types";
import { CommunityProfileImage } from "~/modules/Common/Image";
import { CommunityDescription } from "~/modules/Router/Auth/components/CommunityInfo/CommunityDescription";
import { CommunityDisplayName } from "~/modules/Router/Auth/components/CommunityInfo/CommunityDisplayName";
import { CommunityInviteBackground } from "~/modules/Router/Auth/components/CommunityInfo/CommunityInviteBackground";
import { CommunityMemberCount } from "~/modules/Router/Auth/components/CommunityInfo/CommunityMemberCount";
import { DesktopCommunityJoinButton } from "~/modules/Router/Auth/components/DesktopCommunityJoinButton";
import { DesktopCommunityNicknameModal } from "~/modules/Router/Auth/components/DesktopCommunityNicknameModal";
import { DesktopWalletConnectModal } from "~/modules/Router/Auth/components/DesktopWalletConnectModal";
import { VerificationTitle } from "~/modules/Router/Auth/components/VerificationTitle";
import { AuthDispatchContext } from "~/modules/Router/Auth/contexts/AuthDispatchContext";
import { useCommunityJoin } from "~/modules/Router/Auth/hooks/useCommunityJoin";
import { Icon } from "../../../Common/Icons";
import { ContractConditions, CurrencyConditions } from "./Condition";
import { ConditionBox } from "./ConditionBox";
import { useDaoStore } from "../../../../_store/dao/useDaoStore";
import { generateUrl } from "~/_utils/converter/generateUrl";
import { assertIsDefined } from "~/_utils/assert";

type Props = {
  isCommunityPreview?: boolean;
};

export const DesktopWalletConnect = ({ isCommunityPreview = false }: Props) => {
  const { updateAuthState } = useContext(AuthDispatchContext);
  const [showWalletConnectModal, setShowWalletConnectModal] = useState(false);
  const clickedButtonRef = useRef<"explore" | "join" | null>(null);

  const isEmailAccount = false;

  const { dao } = useDaoStore();
  const {
    handleClickJoinButton: handleJoin,
    handleClickVerifyButton,
    handleClickConnectWalletButton,
    handleClickExplore,
    verifications,
    isSimplifiedLayout,
    hasCurrencyConditions,
    hasContractConditions,
    hasConditions,
    currencyConditions,
    contractConditions,
    message,
    status,
    showNicknameModal,
    needConnectWallet,
    nickNameForm,
    joinError,
    handleClickCancelButton,
  } = useCommunityJoin({ isEmailAccount });

  const handleClickExploreButton = () => {
    if (isCommunityPreview) {
      return;
    }

    assertIsDefined(dao);

    // 조건이 있는 경우
    if (hasConditions) {
      clickedButtonRef.current = "explore";
      setShowWalletConnectModal(true);
    } else {
      /**
       * @TODO ppeeou: AuthGuard 제거시 navigate로 변경
       */
      location.href = generateUrl({
        type: "home",
        subdomain: dao.subdomain,
        explore: true,
      });
    }
  };

  const handleClickJoinButton = async () => {
    if (isCommunityPreview) return;
    if (status === "notSatisfied") {
      handleClickVerifyButton();
    } else {
      clickedButtonRef.current = "join";
      setShowWalletConnectModal(true);
    }
  };

  const handleCloseWalletConnectModal = () => {
    if (isCommunityPreview) return;
    clickedButtonRef.current = null;
    setShowWalletConnectModal(false);
  };

  const hasWalletAccounts = (userId: string) => {
    return new Promise((resolve) => {
      getUserAccounts({ userId, emailAccount: "true" })
        .then((result) => {
          if (
            result.code === "000" &&
            result.data.evmCompatibleAccounts.length
          ) {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(() => {
          resolve(false);
        });
    });
  };

  const handleConnectedWalletConnectModal = async (userId?: string) => {
    if (clickedButtonRef.current === "explore") {
      handleClickExplore();
    }
    if (clickedButtonRef.current === "join") {
      if (userId) {
        if (await hasWalletAccounts(userId)) {
          handleClickVerifyButton();
        } else {
          if (hasConditions) {
            updateAuthState();
          } else {
            handleClickVerifyButton();
          }
        }
      } else {
        handleClickVerifyButton();
      }
    }
  };

  useEffect(() => {
    if (status === "verified") {
      window.location.reload();
    } else {
      if (
        !["loading", "alreadyJoined"].includes(status) &&
        clickedButtonRef.current
      ) {
        handleCloseWalletConnectModal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const { t } = useTranslation([ENameSpace.Common]);

  if (!dao || !verifications) {
    return null;
  }

  return (
    <CommunityInviteBackground
      coverImageUrl={dao.coverImageUrl}
      categoryId={dao.categoryId}
    >
      <article
        className={`${
          isSimplifiedLayout ? "w-[31.25rem]" : "w-[38.25rem]"
        } flex flex-col items-center justify-between gap-y-10 rounded-[2.5rem] bg-ciety-bk bg-opacity-80 p-10 backdrop-blur-[15px]`}
      >
        <div className={`flex-center flex-col gap-y-4`}>
          <CommunityProfileImage
            displayName={dao.displayName}
            categoryId={dao.categoryId ?? "0"}
            size={"xl"}
            imageUrl={dao.profileImageUrl}
          />
          <div className={`flex-center flex-col gap-y-4`}>
            <CommunityDisplayName dao={dao} />
            <CommunityMemberCount dao={dao} fontClassName={"typo-r2-auto"} />
          </div>
          <CommunityDescription dao={dao} fontClassName={"typo-r1"} />
        </div>
        {!isSimplifiedLayout && (
          <>
            <div className="flex w-full flex-col gap-y-4">
              <VerificationTitle>
                {t(
                  `${ENameSpace.Common}:TokenGating.VerificationRequirementsToJoinCommunity`,
                )}
              </VerificationTitle>
              <ConditionBox>
                <CurrencyConditions
                  currencyConditions={currencyConditions}
                  hidden={!hasCurrencyConditions}
                />
                <ContractConditions
                  contractConditions={contractConditions}
                  hidden={!hasContractConditions}
                />
              </ConditionBox>
              <div className="flex items-center gap-x-2 text-ciety-gy">
                <Icon iconType="Alert Sign Circle" className="h-4 w-4" />
                <span className="typo-r2">
                  {t(
                    `${ENameSpace.Common}:TokenGating.WeWillOnlyVerifyYouAndYouWontBeChargedForAnyCost`,
                  )}
                </span>
              </div>
            </div>
            {message.length > 0 && (
              <p className="typo-r2 whitespace-pre-wrap break-words text-center text-ciety-rd">
                {message}
              </p>
            )}
          </>
        )}
        <DesktopCommunityJoinButton
          className="flex h-10 gap-x-2"
          status={status}
          hiddenExploreButton={false}
          needConnectWallet={needConnectWallet}
          handleClickExplore={handleClickExploreButton}
          handleClickConnectWalletButton={handleClickConnectWalletButton}
          handleClickVerifyButton={handleClickJoinButton}
        />
      </article>
      {showNicknameModal && (
        <DesktopCommunityNicknameModal
          dao={dao}
          nickNameForm={nickNameForm}
          joinError={joinError}
          handleClickJoinButton={handleJoin}
          onClose={handleClickCancelButton}
        />
      )}
      {showWalletConnectModal && (
        <DesktopWalletConnectModal
          onConnected={handleConnectedWalletConnectModal}
          onClose={handleCloseWalletConnectModal}
        />
      )}
    </CommunityInviteBackground>
  );
};
